import React, { useState, useEffect, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Trash2, MessageCircle, ShieldCheck, Clock } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { AppHeader } from "../components/elysium/AppHeader";
import { Reveal } from "../components/elysium/primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_DESC = {
  archive: "Archive only — browse stories, no conversation",
  guided: "Guided — AI answers only from recorded stories",
  open: "Open — AI may generalize from tone & values (flagged)",
};

export const SoulKeyDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [sk, setSk] = useState(null);
  const [stories, setStories] = useState([]);
  const [perms, setPerms] = useState([]);
  const [audit, setAudit] = useState([]);
  const [err, setErr] = useState("");

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pMode, setPMode] = useState("guided");
  const [pPost, setPPost] = useState("");

  const load = useCallback(async () => {
    try {
      const skRes = await axios.get(`${API}/soulkeys/${id}`);
      setSk(skRes.data);
      const stRes = await axios.get(`${API}/soulkeys/${id}/stories`);
      setStories(stRes.data);
      if (skRes.data.my_mode === "owner") {
        const [pRes, aRes] = await Promise.all([
          axios.get(`${API}/soulkeys/${id}/permissions`),
          axios.get(`${API}/soulkeys/${id}/audit-log`),
        ]);
        setPerms(pRes.data);
        setAudit(aRes.data);
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [id]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-ely-bg"><Loader2 className="animate-spin text-ely-primary" /></div>;
  if (user === false) return <Navigate to="/login" replace />;

  const isOwner = sk?.my_mode === "owner";
  const canChat = sk && sk.my_mode !== "archive" && sk.my_mode !== "none";

  const addStory = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    await axios.post(`${API}/soulkeys/${id}/stories`, { title: title.trim() || null, content: content.trim() });
    setTitle(""); setContent(""); load();
  };
  const removeStory = async (sid) => {
    await axios.delete(`${API}/soulkeys/${id}/stories/${sid}`); load();
  };
  const grant = async (e) => {
    e.preventDefault();
    if (!pEmail.trim()) return;
    try {
      await axios.post(`${API}/soulkeys/${id}/permissions`, {
        viewer_email: pEmail.trim(), viewer_mode: pMode, posthumous_mode: pPost || null,
      });
      setPEmail(""); setPPost(""); load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const changeStatus = async (status) => {
    await axios.post(`${API}/soulkeys/${id}/status`, { status }); load();
  };

  if (err && !sk) {
    return (
      <div className="min-h-screen bg-ely-bg"><div className="grain" /><AppHeader />
        <div className="mx-auto max-w-5xl px-6 py-20"><p className="font-sans text-red-400">{err}</p>
          <Link to="/app" className="mt-4 inline-block font-mono text-xs uppercase tracking-wider text-ely-primary">← All Soul-Keys</Link>
        </div>
      </div>
    );
  }
  if (!sk) return <div className="flex min-h-screen items-center justify-center bg-ely-bg"><Loader2 className="animate-spin text-ely-primary" /></div>;

  const field = "w-full rounded-md border border-ely-border bg-ely-bg/60 px-4 py-3 font-sans text-base text-ely-text placeholder:text-ely-muted/60 outline-none focus:border-ely-primary mb-3";
  const lbl = "block font-mono text-[10px] uppercase tracking-[0.2em] text-ely-muted mb-2";

  return (
    <div className="min-h-screen bg-ely-bg">
      <div className="grain" aria-hidden="true" />
      <AppHeader />
      <main className="mx-auto max-w-3xl px-6 py-14 md:px-10 md:py-16" data-testid="soulkey-detail">
        <Link to="/app" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-ely-muted transition-colors hover:text-ely-primary" data-testid="back-to-soulkeys">
          <ArrowLeft size={13} /> All Soul-Keys
        </Link>

        <Reveal delay={0.05}>
          <h1 className="mt-6 font-serif text-4xl font-light italic leading-none tracking-tight text-ely-text md:text-6xl">{sk.name}</h1>
        </Reveal>

        <div className="mt-6 border-l-2 border-ely-primary/50 bg-ely-primary/[0.05] px-5 py-4 font-sans text-sm font-light text-ely-text/80">
          This Soul-Key is an AI reflection built from recorded stories — not the person themselves.
        </div>

        {canChat && (
          <Link to={`/soulkey/${id}/chat`}>
            <button data-testid="talk-btn" className="mt-6 flex items-center gap-2 rounded-full bg-ely-primary px-6 py-3 font-sans text-sm font-medium text-ely-bg transition-all hover:bg-ely-primaryHover">
              <MessageCircle size={15} /> Talk with this Soul-Key
            </button>
          </Link>
        )}

        {isOwner && (
          <section className="mt-12">
            <h2 className="font-serif text-2xl font-light text-ely-text">Add a story</h2>
            <form onSubmit={addStory} className="mt-4 rounded-xl border border-ely-border/60 bg-ely-surface/40 p-6" data-testid="add-story-form">
              <label className={lbl}>Title (optional)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Her wedding day" className={field} data-testid="story-title-input" />
              <label className={lbl}>Story</label>
              <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Record the memory in as much detail as you'd like…" className={`${field} min-h-[120px] resize-none`} data-testid="story-content-input" />
              <button type="submit" disabled={!content.trim()} data-testid="save-story-btn" className="rounded-full bg-ely-primary px-6 py-2.5 font-sans text-sm font-medium text-ely-bg disabled:opacity-40">Save Story</button>
            </form>
          </section>
        )}

        <section className="mt-12">
          <h2 className="font-serif text-2xl font-light text-ely-text">Recorded stories ({stories.length})</h2>
          <div className="mt-4 divide-y divide-ely-border/40 rounded-xl border border-ely-border/60 bg-ely-surface/40">
            {stories.length ? stories.map((s) => (
              <div key={s.id} className="flex items-start justify-between gap-4 p-5" data-testid={`story-${s.id}`}>
                <div className="flex-1">
                  <p className="font-serif text-lg text-ely-text">{s.title || "(untitled)"}</p>
                  <p className="mt-1 whitespace-pre-wrap font-sans text-sm font-light text-ely-muted">{s.content}</p>
                </div>
                {isOwner && (
                  <button onClick={() => removeStory(s.id)} data-testid={`delete-story-${s.id}`} className="shrink-0 text-ely-muted transition-colors hover:text-red-400"><Trash2 size={15} /></button>
                )}
              </div>
            )) : <p className="p-5 font-sans text-sm font-light text-ely-muted">No stories recorded yet.</p>}
          </div>
        </section>

        {isOwner && (
          <>
            <section className="mt-12">
              <h2 className="font-serif text-2xl font-light text-ely-text">Who can access this Soul-Key</h2>
              <div className="mt-4 rounded-xl border border-ely-border/60 bg-ely-surface/40 p-6">
                <div className="space-y-2" data-testid="permissions-list">
                  {perms.length ? perms.map((p) => (
                    <div key={p.viewer_email} className="flex items-center justify-between border-b border-ely-border/30 py-2 last:border-0">
                      <span className="font-sans text-sm text-ely-text/80">{p.viewer_email}</span>
                      <span className="rounded-full border border-ely-border px-3 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ely-primary">{p.mode}{p.posthumous_mode ? ` → ${p.posthumous_mode}` : ""}</span>
                    </div>
                  )) : <p className="font-sans text-sm font-light text-ely-muted">No one added yet.</p>}
                </div>
                <form onSubmit={grant} className="mt-6" data-testid="grant-form">
                  <label className={lbl}>Add a family member</label>
                  <input value={pEmail} onChange={(e) => setPEmail(e.target.value)} placeholder="family@example.com" className={field} data-testid="perm-email-input" />
                  <label className={lbl}>Access mode</label>
                  <select value={pMode} onChange={(e) => setPMode(e.target.value)} className={field} data-testid="perm-mode-select">
                    <option value="archive">{MODE_DESC.archive}</option>
                    <option value="guided">{MODE_DESC.guided}</option>
                    <option value="open">{MODE_DESC.open}</option>
                  </select>
                  <label className={lbl}>Posthumous mode (optional — applies once verified deceased)</label>
                  <select value={pPost} onChange={(e) => setPPost(e.target.value)} className={field} data-testid="perm-posthumous-select">
                    <option value="">Same as above</option>
                    <option value="archive">Archive</option>
                    <option value="guided">Guided</option>
                    <option value="open">Open</option>
                  </select>
                  <button type="submit" data-testid="grant-btn" className="rounded-full bg-ely-primary px-6 py-2.5 font-sans text-sm font-medium text-ely-bg">Grant Access</button>
                </form>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="font-serif text-2xl font-light text-ely-text">Status</h2>
              <div className="mt-4 rounded-xl border border-ely-border/60 bg-ely-surface/40 p-6">
                <p className="font-sans text-sm font-light text-ely-muted">Current status: <b className="text-ely-text">{sk.status}</b></p>
                <p className="mt-2 font-sans text-xs font-light text-ely-muted/70">Changing this to a verified deceased state should require real verification in production — this demo lets you flip it directly for testing.</p>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="status-controls">
                  {["living", "deceased_unverified", "deceased_verified"].map((st) => (
                    <button key={st} onClick={() => changeStatus(st)} data-testid={`status-${st}`}
                      className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-wider transition-colors ${sk.status === st ? "border-ely-primary bg-ely-primary/10 text-ely-primary" : "border-ely-border text-ely-muted hover:border-ely-primary hover:text-ely-primary"}`}>
                      {st.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-serif text-2xl font-light text-ely-text"><ShieldCheck size={18} className="text-ely-primary" /> Access log</h2>
              <div className="mt-4 rounded-xl border border-ely-border/60 bg-ely-surface/40 p-6" data-testid="audit-log">
                {audit.length ? (
                  <div className="space-y-2">
                    {audit.map((l, i) => (
                      <div key={i} className="flex items-center gap-3 border-b border-ely-border/30 py-2 text-sm last:border-0">
                        <Clock size={12} className="text-ely-muted" />
                        <span className="font-mono text-[11px] text-ely-primary">{l.action}{l.detail ? `: ${l.detail}` : ""}</span>
                        <span className="text-ely-muted">{l.viewer_email}</span>
                        <span className="ml-auto font-mono text-[10px] text-ely-muted/60">{(l.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="font-sans text-sm font-light text-ely-muted">No access recorded yet.</p>}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};
